/**
 * The firm's PMS contact-type catalogue — the options the contact card's type selector offers.
 *
 * Provider-agnostic, like every other route under `/customer-integrations/pms/:provider/…`: the
 * backend serves whatever the firm's provider declares, and a provider that publishes no catalogue
 * returns an empty list rather than erroring. That empty list **is** the degrade signal the UI gates
 * on — never a provider-name check.
 *
 * The list travels with a `syncedAt`, because two surfaces need different questions answered from
 * the same response: a picker asks *"are there options to offer?"* (the list), while the refresh
 * action asks *"does this provider publish a catalogue at all?"* — and an empty list cannot tell an
 * unasked provider from one with nothing to offer. See `contact-type-catalogue-state.ts`.
 *
 * Throws on failure (`DirectApiError`) rather than returning a result envelope — the `client.ts`
 * convention for `directApiClient`-backed helpers.
 */

'use client';

import { directApiClient } from '@/lib/api/direct-client';
import type {
  PmsContactTypeCatalogue,
  PmsContactTypesResponse,
  PmsProvider
} from './types';

export async function getPmsContactTypes(
  provider: PmsProvider
): Promise<PmsContactTypeCatalogue> {
  const response = await directApiClient.get<PmsContactTypesResponse>(
    `/customer-integrations/pms/${provider}/contact-types`
  );

  return toCatalogue(response);
}

/**
 * Ask the provider for the catalogue **now** and rewrite the stored copy, returning what is now
 * stored.
 *
 * The nightly sweep is the floor: a firm that edits its participant types in the PMS reaches them
 * through this rather than waiting for it. Throws on an unreachable provider — the backend writes
 * nothing on a failed read, so the caller must surface the failure rather than let it read as a
 * completed refresh.
 *
 * Sorted through the same {@link toCatalogue} as the fetch above, so a refreshed list cannot come
 * back ordered differently from the one it replaces.
 */
export async function refreshPmsContactTypes(
  provider: PmsProvider
): Promise<PmsContactTypeCatalogue> {
  const response = await directApiClient.post<PmsContactTypesResponse>(
    `/customer-integrations/pms/${provider}/contact-types/refresh`
  );

  return toCatalogue(response);
}

function toCatalogue(
  response: PmsContactTypesResponse
): PmsContactTypeCatalogue {
  /*
    **Sorted here, at the one place both surfaces read** (VALE-158). A firm's
    catalogue arrives in whatever order the provider enumerates it, which is
    neither alphabetical nor stable, and a list nobody can predict the order of
    is one the reader has to scan end-to-end every time.

    It belongs to this fetch rather than to either picker's own spec: the
    contact card (`PmsContactTypeSelector`) and the drawer's attribute path both
    render straight off this array, so sorting at a call site would leave the
    two offering the same catalogue in two different orders — the drift a single
    source of truth exists to prevent.

    Compared with an explicit `'en'` locale rather than the ambient one, so the
    order is a property of the data and not of the machine rendering it.
  */
  return {
    contactTypes: [...response.contactTypes].sort((a, b) =>
      a.name.localeCompare(b.name, 'en', { sensitivity: 'base' })
    ),
    syncedAt: response.syncedAt ?? null
  };
}
